
import { useAuth } from "@/context/auth/AuthContext";
import { useOfflineStatus } from '@/context/offline/OfflineProvider';
import { UserCategory } from '@/types';
import { useCategoryState } from './categories/useCategoryState';
import { useCategoryLoader } from './categories/useCategoryLoader';
import { useCategoryOperations } from './categories/useCategoryOperations';
import { useCategorySync } from './categories/useCategorySync';
import { useCategoryEvents } from './categories/useCategoryEvents';

interface UseOfflineCategoriesResult {
  categories: UserCategory[];
  isLoading: boolean;
  isSyncing: boolean;
  error: any;
  addCategory: (category: Omit<UserCategory, 'id'>) => Promise<string | undefined>;
  updateCategory: (id: string, updates: Partial<UserCategory>) => Promise<boolean>;
  deleteCategory: (id: string) => Promise<boolean>;
  createCategory: (categoryData: { name: string; icon: string; color: string; text_color: string }) => Promise<UserCategory>;
  forceSyncCategories: () => Promise<void>;
  syncOnlyCategories: () => Promise<void>;
  lastSyncTimestamp: number;
  pendingOperationsCount: number;
  batchUpdatePositions: (updates: Array<{ id: string; position: number }>) => Promise<boolean>;
  refreshCategories: () => Promise<void>;
  mutateCategoryLocally: (id: string, updates: Partial<UserCategory>) => void;
  setCategories: (categories: UserCategory[]) => void;
}

export const useOfflineCategories = (): UseOfflineCategoriesResult => {
  const { user } = useAuth();
  const { dbInitialized } = useOfflineStatus();

  // State management
  const {
    categories,
    setCategories,
    isLoading,
    setIsLoading,
    error,
    setError,
    pendingOperationsCount,
    setPendingOperationsCount,
    handleCategoryAdded,
    handleCategoryUpdated,
    handleCategoryDeleted,
    mutateCategoryLocally
  } = useCategoryState();

  // Sync functionality
  const {
    forceSyncCategories,
    syncOnlyCategories,
    updatePendingCountFromService,
    isSyncing,
    lastSyncTimestamp
  } = useCategorySync({
    userId: user?.id,
    setCategories,
    updatePendingCount: async () => {
      const count = await updatePendingCountFromService();
      setPendingOperationsCount(count);
    }
  });

  // Loading functionality
  const { loadCategories, refreshCategories } = useCategoryLoader({
    dbInitialized,
    userId: user?.id,
    categories,
    setCategories,
    setIsLoading,
    setError,
    updatePendingCount: async () => {
      const count = await updatePendingCountFromService();
      setPendingOperationsCount(count);
    }
  });

  // Operations functionality
  const {
    addCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    batchUpdatePositions
  } = useCategoryOperations({
    onCategoryAdded: handleCategoryAdded,
    onCategoryUpdated: handleCategoryUpdated,
    onCategoryDeleted: handleCategoryDeleted,
    onPendingCountUpdate: async () => {
      const count = await updatePendingCountFromService();
      setPendingOperationsCount(count);
    }
  });

  // Event handling
  useCategoryEvents({
    categories,
    pendingOperationsCount,
    loadCategories,
    updatePendingCount: async () => {
      const count = await updatePendingCountFromService();
      setPendingOperationsCount(count);
    },
    syncOnlyCategories,
    setCategories
  });

  return {
    categories,
    isLoading,
    isSyncing,
    error,
    addCategory,
    updateCategory,
    deleteCategory,
    mutateCategoryLocally,
    createCategory,
    forceSyncCategories,
    syncOnlyCategories,
    lastSyncTimestamp,
    pendingOperationsCount,
    batchUpdatePositions,
    refreshCategories,
    setCategories
  };
};
